import { useState, useEffect } from "react";
import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { ListFilter as ListFilterIcon, ArrowDownWideNarrow as SortIcon } from "lucide-react";
import {
  genFlatOptionList,
  genIdOptionList,
  CommonDestinationTimezone,
} from "@/lib/utils";
import { CommodityOptions } from "@/lib/api/commodity";
import {
  useCreateGeneralContract,
  useUpdateGeneralContract,
  useDownloadOpenRecentContractData,
  useDownloadOpenImportContractData,
  useListLocations,
  useListMarketZones,
  Location,
  MarketZone,
  purchaseSaleOptions,
  uomOptions,
  marketingFeeOptions,
  currencyOptions,
  generateContractTypesWith,
  calculateOnOptions,
  rateTypeOptions,
  costCategoryOptions,
  addDedADjOptions,
  deliveryTermsOptions,
  mzVendorOptions,
  Currency,
  Uom,
} from "@/lib/api/generalContract";
import { Trader, Customer } from "@/lib/api/reports";
import { FormGridHelper } from "@/section/reports/helper";
import {
  FilterFormSchema,
  SortFormSchema,
  SortingOptions,
} from "@/lib/schemas/generalContract";
import { getUserStytchName } from "@/lib/models/auth";
import { BearDatePicker } from "@/lib/form/ui";
import { SeparatorWithText } from "@/components/ui/separator";
import { Loader } from "@/components/ui/loader";
import { RecentReportQueryValidation } from "@/lib/api/otc";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PrimitiveDialogForm } from "@/lib/form/layout";
import { formulateOptionMonths } from "@/lib/api/common";
import {
  useSortFilterState,
  sortOptionsState,
  filterOptionsState,
  downloadImportState,
  createState,
  updateState,
  previewState,
} from "./useGeneralContractState";
import { BaseFormSchema, DownloadRecentFormSchema } from "./GeneralContractSchemas";

function DownloadImportDynamicForm({
  onCloseForm,
  onSubmitForm,
  onSuccess,
}: {
  preventSubmitClose?: boolean;
  onCloseForm: any;
  onSubmitForm: any;
  onSuccess: any;
}) {
  return (
    <FormGridHelper
      onSuccess={onSuccess}
      state={downloadImportState}
      activeSchema={DownloadRecentFormSchema}
      id={"DownloadImportGeneralContract"}
      externalReferences={{}}
      failMessage="Failed to generate import contract"
      successMessage="Generated"
      onSubmitTo={onSubmitForm}
      onCloseForm={onCloseForm}
      preventSubmitClose={true}
    />
  );
}

function DownloadRecentDynamicForm({
  onCloseForm,
  onSubmitForm,
  onSuccess,
  fields,
  setFields,
  isSubmitting,
  isDisabled,
}: {
  preventSubmitClose?: boolean;
  onCloseForm: any;
  onSubmitForm: any;
  onSuccess: any;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  fields: {
    created_date?: Date | null;
    created_range?: { start?: Date; end?: Date } | null;
  };
  setFields: any;
}) {
  return (
    <form onSubmit={onSubmitForm}>
      <div className="grid grid-cols-2 gap-3 col-span-2 content-center">
        <div className="lg:col-span-2">
          <BearDatePicker
            tabIndex={0}
            name="recent_download_filters_contract_end_date"
            label="Single Created Date"
            value={fields.created_date as any}
            onValueChange={(date) =>
              setFields({ created_date: date, created_range: null })
            }
          />
        </div>
        <div className="lg:col-span-2">
          <SeparatorWithText>OR</SeparatorWithText>
        </div>
        <div className="flex flex-col lg:flex-row gap-4 lg:col-span-2">
          <BearDatePicker
            tabIndex={2}
            name="recent_download_filters_contract_end_date"
            label="Created Start"
            value={fields.created_range?.start || ("" as any)}
            onValueChange={(date) => {
              if (date && !fields.created_range?.end) {
                return setFields({
                  created_date: null,
                  created_range: {
                    start: date,
                    end: moment(date).endOf("month").toDate(),
                  },
                });
              }
              return setFields({
                created_date: null,
                created_range: {
                  start: date,
                  end: fields.created_range?.end,
                },
              });
            }}
          />
          <BearDatePicker
            tabIndex={3}
            name="recent_download_filters_contract_end_date"
            label="Created End"
            value={fields.created_range?.end || ("" as any)}
            onValueChange={(date) =>
              setFields({
                created_date: null,
                created_range: {
                  end: date,
                  start: fields.created_range?.start,
                },
              })
            }
          />
        </div>
      </div>
      <div className="flex flex-row justify-between items-center mt-4">
        {!!isSubmitting && <Loader />}
        {!isSubmitting && (
          <Button
            variant="outline"
            type="submit"
            size="sm"
            className="px-3 col-start-4"
            disabled={isSubmitting || isDisabled}
          >
            Generate
          </Button>
        )}
      </div>
    </form>
  );
}

function DownloadRecentReportsModal() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [links, setLinks] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const onGenerateLinks = useDownloadOpenRecentContractData();
  const [fields, setFields] = useState<{
    created_date?: Date | null;
    created_range?: { start?: Date; end?: Date } | null;
  }>({ created_date: moment().toDate() });

  const onSuccess = (res: any) => {
    if (res.import_recent_link) {
      setLinks([{ label: "Download Recent", link: res.import_recent_link }]);
    } else {
      setLinks([]);
    }
  };

  const isDisabled =
    !fields.created_date &&
    !fields.created_range?.start &&
    !fields.created_range?.end;

  const onSubmitForm = async (e: any) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    try {
      if (isSubmitting) return;
      if (isDisabled) return;
      setIsSubmitting(true);

      const queryResult = RecentReportQueryValidation.safeParse({
        created_date: fields.created_date
          ? moment(fields.created_date).format("YYYY/MM/DD")
          : undefined,
        created_range:
          (fields.created_range?.start || fields.created_range?.end)
            ? {
                start: fields.created_range?.start
                  ? moment(fields.created_range?.start).format("YYYY/MM/DD")
                  : undefined,
                end: fields.created_range?.end
                  ? moment(fields.created_range?.end).format("YYYY/MM/DD")
                  : undefined,
              }
            : undefined,
      });

      if (!queryResult.success) {
        toast({
          title: "Invalid Filters",
          description: "",
        });
        return;
      }

      const result = await onGenerateLinks(queryResult.data);

      if (!result.success) {
        throw result;
      } else {
        setLinks([
          { label: "Download Recent", link: result.data.import_recent_link },
        ]);
      }
    } catch (error) {
      console.error("Failed to generate otc recent report", error);
      toast({
        title: "Failed to generate",
        description: "",
      });
      setLinks([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PrimitiveDialogForm
      className=""
      isOpen={isOpen}
      onDialogChange={setIsOpen}
      buttonLabel="Download Recent"
      dialogTitle={"Download Contracts For"}
      footer={
        <span>
          {links.map((wrapper) => {
            return (
              <a
                key={`DownloadRecent:${wrapper.label}`}
                href={wrapper.link}
                target="_blank"
                className="underline underline-offset-1 decoration-sky-500"
              >
                Download Report
              </a>
            );
          })}
        </span>
      }
      form={
        <DownloadRecentDynamicForm
          fields={fields}
          setFields={setFields}
          isSubmitting={isSubmitting}
          isDisabled={isDisabled}
          onCloseForm={setIsOpen}
          onSubmitForm={onSubmitForm}
          onSuccess={onSuccess}
        />
      }
    />
  );
}

function SortDynamicForm({ onCloseForm }: { onCloseForm: any }) {
  const setSortState = useSortFilterState((state: any) => state.setSortState);
  return (
    <FormGridHelper
      state={sortOptionsState}
      activeSchema={SortFormSchema}
      id={"SortGeneralContracts"}
      externalReferences={{
        SortingOptions,
      }}
      failMessage="Failed to apply sorting"
      successMessage="Sorting"
      onSubmitTo={(sortOptions: any) => {
        setSortState(sortOptions);
        return { success: true };
      }}
      onCloseForm={onCloseForm}
      preventSubmitClose={false}
      preventClearForm
    />
  );
}

function FilterDynamicForm({
  onCloseForm,
  traders,
  customers,
  locations,
}: {
  onCloseForm: any;
  locations: Location[];
  customers: Customer[];
  traders: Trader[];
}) {
  const setFilterState = useSortFilterState(
    (state: any) => state.setFilterState
  );

  return (
    <FormGridHelper
      state={filterOptionsState}
      activeSchema={FilterFormSchema}
      id={"FilterGeneralContracts"}
      externalReferences={{
        locations,
        locationOptions: genIdOptionList(locations, "long_name"),
        customerOptions: genIdOptionList(customers, "long_name"),
        commodityOptions: CommodityOptions,
        traderOptions: genIdOptionList(traders, "name"),
        optionMonthOptions: formulateOptionMonths(),
        contractTypeOptions: genFlatOptionList(generateContractTypesWith({})),
      }}
      failMessage="Failed to apply filters"
      successMessage="Filtering"
      onSubmitTo={(sortOptions: any) => {
        setFilterState(sortOptions);
        return { success: true };
      }}
      onCloseForm={onCloseForm}
      preventSubmitClose={false}
      preventClearForm
    />
  );
}

function CreateDynamicForm({
  onCloseForm,
  traders,
  customers,
  locations,
  marketZones,
}: {
  onCloseForm: any;
  locations: Location[];
  marketZones: MarketZone[];
  customers: Customer[];
  traders: Trader[];
}) {
  const createApiContract = useCreateGeneralContract();
  const populateWith: any = createState.useInputsState(
    (state: any) => state.populateWith
  );
  const quantity: any = createState.useInputsState(
    (state: any) => state.formValues?.quantity
  );
  const { member } = useStytchMember();
  const member_name = getUserStytchName(member);

  useEffect(() => {
    if (!quantity.value) {
      const trader_id = traders.find(
        (trader: Trader) => trader.name === member_name
      )?.id;
      populateWith({
        purchase_sale_type: "P",
        contract_date: moment.tz(CommonDestinationTimezone).toDate(),
        currency: Currency.UsDollar,
        trader_id,
        uom: Uom.Bushel,
        price_uom: Uom.Bushel,
        contract_marketing_fee_setting: "1.5",
      });
    }
  }, []);

  return (
    <FormGridHelper
      state={createState}
      activeSchema={BaseFormSchema}
      id={"CreateGeneralContract"}
      externalReferences={{
        locations,
        marketZones,
        marketZonesOptions: genIdOptionList(marketZones, "market_zone"),
        locationOptions: genIdOptionList(locations, "long_name"),
        customerOptions: genIdOptionList(customers, "long_name"),
        commodityOptions: CommodityOptions,
        purchaseSaleOptions,
        uomOptions,
        marketingFeeOptions,
        currencyOptions,
        costCategoryOptions,
        rateTypeOptions,
        calculateOnOptions,
        addDedADjOptions,
        deliveryTermsOptions,
        mzVendorOptions,
        traderOptions: genIdOptionList(traders, "name").sort((a,b) => a.label.localeCompare(b.label)),
        optionMonthOptions: formulateOptionMonths(),
        contractTypeOptions: genFlatOptionList(generateContractTypesWith({})),
      }}
      failMessage="Failed to create contract"
      successMessage="Created"
      onSubmitTo={createApiContract}
      onCloseForm={onCloseForm}
      preventSubmitClose={false}
    />
  );
}

function UpdateDynamicForm({
  onCloseForm,
  traders,
  customers,
  locations,
  marketZones,
}: {
  onCloseForm: any;
  locations: Location[];
  marketZones: MarketZone[];
  customers: Customer[];
  traders: Trader[];
}) {
  const updateApiContract = useUpdateGeneralContract();
  return (
    <FormGridHelper
      state={updateState}
      id={"UpdateGeneralContract"}
      activeSchema={BaseFormSchema}
      externalReferences={{
        locations,
        marketZones,
        marketZonesOptions: genIdOptionList(marketZones, "market_zone"),
        locationOptions: genIdOptionList(locations, "long_name"),
        customerOptions: genIdOptionList(customers, "long_name"),
        commodityOptions: CommodityOptions,
        purchaseSaleOptions,
        uomOptions,
        marketingFeeOptions,
        currencyOptions,
        costCategoryOptions,
        rateTypeOptions,
        addDedADjOptions,
        calculateOnOptions,
        deliveryTermsOptions,
        mzVendorOptions,
        traderOptions: genIdOptionList(traders, "name"),
        optionMonthOptions: formulateOptionMonths(),
        contractTypeOptions: genFlatOptionList(generateContractTypesWith({})),
      }}
      failMessage="Failed to update contract"
      successMessage="Updated"
      onSubmitTo={updateApiContract}
      onCloseForm={onCloseForm}
      preventSubmitClose={false}
    />
  );
}

function PreviewDynamicForm({
  onCloseForm,
  traders,
  customers,
  locations,
  marketZones,
}: {
  onCloseForm: any;
  locations: Location[];
  marketZones: MarketZone[];
  customers: Customer[];
  traders: Trader[];
}) {
  return (
    <FormGridHelper
      isSubmittingDisabled
      state={previewState}
      id={"PreviewGeneralContract"}
      activeSchema={BaseFormSchema}
      externalReferences={{
        locations,
        marketZones,
        marketZonesOptions: genIdOptionList(marketZones, "market_zone"),
        locationOptions: genIdOptionList(locations, "long_name"),
        customerOptions: genIdOptionList(customers, "long_name"),
        commodityOptions: CommodityOptions,
        purchaseSaleOptions,
        uomOptions,
        marketingFeeOptions,
        currencyOptions,
        costCategoryOptions,
        rateTypeOptions,
        addDedADjOptions,
        calculateOnOptions,
        deliveryTermsOptions,
        mzVendorOptions,
        traderOptions: genIdOptionList(traders, "name"),
        optionMonthOptions: formulateOptionMonths(),
        contractTypeOptions: genFlatOptionList(generateContractTypesWith({})),
      }}
      failMessage=""
      successMessage=""
      onSubmitTo={async () => ({ success: true })}
      onCloseForm={onCloseForm}
      preventSubmitClose={true}
      hideSkipButton
    />
  );
}

function SortFormModal() {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <PrimitiveDialogForm
      className="max-w-[98%] h-[98%]"
      isOpen={isOpen}
      onDialogChange={setIsOpen}
      buttonIcon={<SortIcon />}
      dialogTitle={"Sort Options"}
      form={<SortDynamicForm onCloseForm={setIsOpen} />}
    />
  );
}

function FilterFormModal({
  customers,
  traders,
  locations,
}: {
  locations: Location[];
  customers: Customer[];
  traders: Trader[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <PrimitiveDialogForm
      className="max-w-[98%] h-[98%]"
      isOpen={isOpen}
      onDialogChange={setIsOpen}
      buttonIcon={<ListFilterIcon />}
      dialogTitle={"Filter Options"}
      form={
        <FilterDynamicForm
          onCloseForm={setIsOpen}
          locations={locations}
          customers={customers}
          traders={traders}
        />
      }
    />
  );
}

function DownloadImportReportsModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [links, setLinks] = useState<any[]>([]);
  const onGenerateLinks = useDownloadOpenImportContractData();

  const onSuccess = (res: any) => {
    if (res.import_new_contracts_link) {
      setLinks([
        { label: "Download Import", link: res.import_new_contracts_link },
      ]);
    } else {
      setLinks([]);
    }
  };

  return (
    <PrimitiveDialogForm
      className=""
      isOpen={isOpen}
      onDialogChange={setIsOpen}
      buttonLabel="Download Import"
      dialogTitle={"Download Import For"}
      footer={
        <span>
          {links.map((wrapper) => {
            return (
              <a
                key={`DownloadImport:${wrapper.label}`}
                href={wrapper.link}
                target="_blank"
                className="underline underline-offset-1 decoration-sky-500"
              >
                Download Report
              </a>
            );
          })}
        </span>
      }
      form={
        <DownloadImportDynamicForm
          onCloseForm={setIsOpen}
          onSubmitForm={onGenerateLinks}
          onSuccess={onSuccess}
        />
      }
    />
  );
}

function ReviewModal({
  reviewModalId,
  setReviewModalId,
  isReviewing,
  onReview,
}: {
  reviewModalId: string | undefined;
  setReviewModalId: any;
  isReviewing: boolean;
  onReview: any;
}) {
  return (
    <Dialog
      open={!!reviewModalId}
      onOpenChange={(status) => {
        if (!status) {
          setReviewModalId(undefined);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you sure you want to mark as Reviewed</DialogTitle>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            if (e?.preventDefault) {
              e.preventDefault();
            }
            return onReview({ id: reviewModalId });
          }}
        >
          <Button type="submit" disabled={isReviewing}>
            {isReviewing ? "Processing" : "Confirm"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ClearSortFilterButton() {
  const clearFunc = useSortFilterState((state: any) => state.clearAll);
  const sortStateClear: any = sortOptionsState.useInputsState(
    (state: any) => state.clearForm
  );
  const filterStateClear: any = filterOptionsState.useInputsState(
    (state: any) => state.clearForm
  );

  return (
    <Button
      variant="secondary"
      onClick={() => {
        clearFunc();
        sortStateClear();
        filterStateClear();
      }}
      disabled={false}
    >
      Clear Sort/Filters
    </Button>
  );
}

export interface GeneralContractModalsProps {
  locations: Location[];
  marketZones: MarketZone[];
  customers: Customer[];
  traders: Trader[];
  onDuplicate: (row: any) => void;
  onArchive: ({ id }: { id: string }) => Promise<void>;
  onDelete: ({ id }: { id: string }) => Promise<void>;
  onReview: ({ id }: { id: string }) => Promise<void>;
  isArchiving: boolean;
  isReviewing: boolean;
  reviewModalId: string | undefined;
  setReviewModalId: any;
  createPopulateWith: any;
  updatePopulateWith: any;
  isCreateOpen: boolean;
  setIsCreateOpen: (value: boolean) => void;
  isUpdateOpen: boolean;
  setIsUpdateOpen: (value: boolean) => void;
}

export interface ContractPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  locations: Location[];
  marketZones: MarketZone[];
  customers: Customer[];
  traders: Trader[];
  initialData?: Record<string, unknown>;
}

export function ContractPreviewModal({
  isOpen,
  onClose,
  locations,
  marketZones,
  customers,
  traders,
  initialData,
}: ContractPreviewModalProps) {
  const previewClearForm: any = previewState.useInputsState(
    (state: any) => state.clearForm
  );
  const previewPopulateWith: any = previewState.useInputsState(
    (state: any) => state.populateWith
  );

  const handleClose = () => {
    onClose();
    previewClearForm();
  };

  // Populate form with initial data when the modal opens and data is available
  useEffect(() => {
    if (isOpen && initialData) {
      previewPopulateWith(initialData);
    }
  }, [isOpen, initialData, previewPopulateWith]);

  return (
    <Dialog open={isOpen} onOpenChange={(value) => !value && handleClose()}>
      <DialogContent className="max-w-[98%] h-[98%] flex flex-col">
        <DialogHeader>
          <DialogTitle>Contract Preview (Read Only)</DialogTitle>
        </DialogHeader>
        <PreviewDynamicForm
          onCloseForm={handleClose}
          locations={locations}
          customers={customers}
          marketZones={marketZones}
          traders={traders}
        />
      </DialogContent>
    </Dialog>
  );
}

export { previewState };

export function GeneralContractModals({
  locations,
  marketZones,
  customers,
  traders,
  onDuplicate,
  onArchive,
  onDelete,
  onReview,
  isArchiving,
  isReviewing,
  reviewModalId,
  setReviewModalId,
  createPopulateWith,
  updatePopulateWith,
  isCreateOpen,
  setIsCreateOpen,
  isUpdateOpen,
  setIsUpdateOpen,
}: GeneralContractModalsProps) {
  const createClearForm: any = createState.useInputsState(
    (state: any) => state.clearForm
  );
  const updateClearForm: any = updateState.useInputsState(
    (state: any) => state.clearForm
  );

  return (
    <>
      <DownloadImportReportsModal />
      <DownloadRecentReportsModal />

      <PrimitiveDialogForm
        className="max-w-[98%] h-[98%]"
        isOpen={isCreateOpen}
        onDialogChange={(value: any) => {
          setIsCreateOpen(value);
          if (!value) {
            createClearForm();
          }
        }}
        buttonLabel={"Create Contract"}
        dialogTitle={"Create Contract"}
        form={
          <CreateDynamicForm
            onCloseForm={(value: any) => {
              setIsCreateOpen(value);
              if (!value) {
                createClearForm();
              }
            }}
            locations={locations}
            customers={customers}
            marketZones={marketZones}
            traders={traders}
          />
        }
      />

      <PrimitiveDialogForm
        className="max-w-[98%] h-[98%]"
        isOpen={isUpdateOpen}
        onDialogChange={(value: any) => {
          if (!value) {
            setIsUpdateOpen(false);
            updateClearForm();
          }
        }}
        dialogTitle={"Update Contract"}
        form={
          <UpdateDynamicForm
            onCloseForm={(value: any) => {
              if (!value) {
                setIsUpdateOpen(false);
                updateClearForm();
              }
            }}
            locations={locations}
            customers={customers}
            marketZones={marketZones}
            traders={traders}
          />
        }
      />

      {/* Commented out: DataTable now handles sorting/filtering internally
      <SortFormModal />
      <FilterFormModal
        locations={locations}
        customers={customers}
        traders={traders}
      />
      <ClearSortFilterButton />
      */}
      <ReviewModal
        reviewModalId={reviewModalId}
        setReviewModalId={setReviewModalId}
        isReviewing={isReviewing}
        onReview={onReview}
      />
    </>
  );
}

